// Engine puro de achievements · evalúa locked/unlocked desde UserProgress.
// Owner: Carlos Beltrán · A2 quick wins · 2026-05-25.
//
// NOTA(andres): cuando exista `GET /api/me/achievements` con `unlocked_at`
// persistido server-side · sustituir el cálculo client-side por fetch directo.
// Este engine queda como fallback y como source-of-truth para decidir qué
// reglas marcan cada badge.

import type { ModuleProgress, UserProgress } from './types';

export type AchievementSlug =
  | 'first-module'
  | 'demo-phase-done'
  | 'real-phase-started'
  | 'streak-7d'
  | 'all-modules'
  | 'first-comment';

export interface Achievement {
  slug: AchievementSlug;
  title: string;
  description: string;
  icon: string;
  unlocked: boolean;
}

export const ACHIEVEMENT_DEFS: Omit<Achievement, 'unlocked'>[] = [
  {
    slug: 'first-module',
    title: 'Primer módulo',
    description: 'Completa tu primer módulo del campus.',
    icon: '🎯',
  },
  {
    slug: 'demo-phase-done',
    title: 'Fase demo desbloqueada',
    description: 'Finaliza los módulos 1-3 de la fase demo.',
    icon: '🚀',
  },
  {
    slug: 'real-phase-started',
    title: 'A producción',
    description: 'Marca el primer checklist de la fase real.',
    icon: '💼',
  },
  {
    slug: 'streak-7d',
    title: 'Constancia',
    description: 'Mantén una racha de 7 días seguidos.',
    icon: '🔥',
  },
  {
    slug: 'all-modules',
    title: 'Maestría completa',
    description: 'Completa los 6 módulos del campus.',
    icon: '🏆',
  },
  {
    slug: 'first-comment',
    title: 'Voz en la comunidad',
    description: 'Comparte tu primer comentario (próximamente).',
    icon: '💬',
  },
];

export interface AchievementContext {
  progress: UserProgress;
  streak?: number;
  /** D1/D2 aún no mergeado · cuando lo esté, pasar count real. */
  commentsCount?: number;
}

export function evaluateAchievements(
  ctx: AchievementContext,
): Achievement[] {
  const { progress, streak = 0, commentsCount = 0 } = ctx;
  const completed = progress.modules.filter(
    (m: ModuleProgress) => m.status === 'completed',
  );
  const completedCount = completed.length;

  const demoModules = progress.modules.filter((m) => m.phase === 'demo');
  const demoDoneCount = demoModules.filter(
    (m) => m.status === 'completed',
  ).length;
  const demoPhaseDone =
    demoModules.length > 0 && demoDoneCount === demoModules.length;

  // 'A producción' se desbloquea cuando hay actividad REAL en la fase 'real':
  // un módulo completado o algún check del checklist marcado. NO basta con
  // que el módulo esté `status='in_progress'` — el backend devuelve ese
  // status por defecto a cualquier módulo desbloqueado y sin completar
  // (serializer _derive_module_status línea 102) aunque el alumno no haya
  // empezado, lo que provocaba que el achievement saliera unlocked desde el
  // primer login. Backlog backend: F-CAMPUS-MODULE-STATUS-AVAILABLE-001
  // (añadir status='available' al serializer y actualizar ModuleStatus).
  const realPhaseStarted = progress.modules.some(
    (m) =>
      m.phase === 'real' &&
      (m.status === 'completed' || m.checklist.some((c) => c.done)),
  );

  const allDone =
    progress.modules.length > 0 &&
    completedCount === progress.modules.length;

  const unlockedMap: Record<AchievementSlug, boolean> = {
    'first-module': completedCount >= 1,
    'demo-phase-done': demoPhaseDone,
    'real-phase-started': realPhaseStarted,
    'streak-7d': streak >= 7,
    'all-modules': allDone,
    'first-comment': commentsCount >= 1,
  };

  return ACHIEVEMENT_DEFS.map((def) => ({
    ...def,
    unlocked: unlockedMap[def.slug],
  }));
}

// ───────────────────────────────────────────────────────────────────────────
// Logros POR CURSO (Jaime · 2026-06-17)
//
// Antes la sección "Logros del curso" (Curso.tsx · CourseAchievementsSection)
// reutilizaba el engine genérico de arriba → las MISMAS 6 tarjetas en todos
// los cursos. Jaime pidió logros propios de cada curso. Cada curso define sus
// 6 tarjetas; el desbloqueo se calcula con señales FIABLES por curso a partir
// del `UserProgress` de ESE curso (módulos completados, fase demo/real, racha).
//
// Las señales son aproximaciones honestas (no hay aún un evento por hito tipo
// "VPS configurado" o "2 robots en real"): se mapea cada tarjeta a la señal de
// progreso más cercana. Cuando el backend exponga hitos finos, afinar el `rule`.
// ───────────────────────────────────────────────────────────────────────────

/** Forma mínima que necesita AchievementBadge para pintar una tarjeta. */
export interface BadgeAchievement {
  slug: string;
  title: string;
  description: string;
  icon: string;
  unlocked: boolean;
}

/** Señales de progreso derivadas del UserProgress de un curso. */
interface ProgressSignals {
  /** Completó al menos un módulo. */
  started: boolean;
  /** Avance temprano (≈1/3 del curso). */
  earlyProgress: boolean;
  /** Toda la fase demo completada. */
  demoDone: boolean;
  /** Hay actividad real en la fase 'real' (módulo completado o checklist). */
  realStarted: boolean;
  /** Racha de 7 días o más. */
  streak7: boolean;
  /** Casi completo (≥80%). */
  nearComplete: boolean;
  /** Todos los módulos completados. */
  allDone: boolean;
}

function computeSignals(ctx: AchievementContext): ProgressSignals {
  const { progress, streak = 0 } = ctx;
  const mods = progress.modules;
  const total = mods.length;
  const completed = mods.filter((m) => m.status === 'completed').length;
  const frac = total > 0 ? completed / total : 0;

  const demoMods = mods.filter((m) => m.phase === 'demo');
  const demoDone =
    demoMods.length > 0 &&
    demoMods.every((m) => m.status === 'completed');

  // Mismo criterio robusto que el engine genérico: NO basta status
  // 'in_progress' (el serializer lo pone por defecto a módulos desbloqueados).
  const realStarted = mods.some(
    (m) =>
      m.phase === 'real' &&
      (m.status === 'completed' || m.checklist.some((c) => c.done)),
  );

  return {
    started: completed >= 1,
    earlyProgress: completed >= 2 || frac >= 0.34,
    demoDone,
    realStarted,
    streak7: streak >= 7,
    nearComplete: total > 0 && frac >= 0.8,
    allDone: total > 0 && completed === total,
  };
}

type SignalKey = keyof ProgressSignals;

interface CourseAchievementDef {
  slug: string;
  title: string;
  description: string;
  icon: string;
  /** Señal que desbloquea esta tarjeta. */
  rule: SignalKey;
}

/**
 * Definiciones de logros por curso. Orden de visualización = orden aprobado
 * por Jaime. El `rule` es la señal de progreso que desbloquea cada tarjeta.
 */
export const COURSE_ACHIEVEMENTS: Record<string, CourseAchievementDef[]> = {
  'robotea-start': [
    { slug: 'start-primer-paso', icon: '🎯', title: 'Primer paso', description: 'Completas tu primer módulo de Start.', rule: 'started' },
    { slug: 'start-vps', icon: '🖥️', title: 'VPS listo', description: 'Configuras tu VPS y MetaTrader.', rule: 'earlyProgress' },
    { slug: 'start-robot-demo', icon: '🤖', title: 'Robot en demo', description: 'Activas tu robot en demo, sin riesgo.', rule: 'demoDone' },
    { slug: 'start-real', icon: '💼', title: 'Salto a real', description: 'Pasas tu robot a cuenta real.', rule: 'realStarted' },
    { slug: 'start-revision', icon: '🔁', title: 'Revisión', description: 'Entras a revisar 7 días seguidos.', rule: 'streak7' },
    { slug: 'start-completado', icon: '🏁', title: 'Start completado', description: 'Terminas todos los módulos de Start.', rule: 'allDone' },
  ],
  'robotea-basic': [
    { slug: 'basic-arranque', icon: '🎯', title: 'Arranque Basic', description: 'Completas tu primer módulo de Basic.', rule: 'started' },
    { slug: 'basic-inicio-cartera', icon: '🤝', title: 'Inicio de cartera', description: 'Activas tu primer robot Basic.', rule: 'earlyProgress' },
    { slug: 'basic-gestion-riesgo', icon: '⚖️', title: 'Gestión de riesgo', description: 'Completas el módulo de gestión de cartera.', rule: 'demoDone' },
    { slug: 'basic-multirobot-real', icon: '📊', title: 'Multi-robot en real', description: 'Tienes dos robots en cuenta real.', rule: 'realStarted' },
    { slug: 'basic-seguimiento', icon: '🔁', title: 'Seguimiento', description: 'Entras a revisar 7 días seguidos.', rule: 'streak7' },
    { slug: 'basic-completado', icon: '🏁', title: 'Basic completado', description: 'Terminas todos los módulos de Basic.', rule: 'allDone' },
  ],
  'robotea-pro': [
    { slug: 'pro-arranque', icon: '🎯', title: 'Arranque Pro', description: 'Completas tu primer módulo de Pro.', rule: 'started' },
    { slug: 'pro-multicuenta', icon: '🏦', title: 'Multi-cuenta', description: 'Operas varias cuentas a la vez.', rule: 'earlyProgress' },
    { slug: 'pro-bateria', icon: '🧰', title: 'Batería completa', description: 'Instalas tus primeros robots Pro.', rule: 'demoDone' },
    { slug: 'pro-masterclass', icon: '📈', title: 'Masterclass Portafolio', description: 'Completas la clase de portafolio.', rule: 'realStarted' },
    { slug: 'pro-monetizacion', icon: '💰', title: 'Monetización', description: 'Completas el módulo de monetización.', rule: 'nearComplete' },
    { slug: 'pro-completado', icon: '🏁', title: 'Pro completado', description: 'Terminas todos los módulos de Pro.', rule: 'allDone' },
  ],
};

/**
 * Evalúa los logros de un curso concreto. Si el curso no tiene definición
 * propia (p. ej. roboteam u otros), cae al engine genérico para no quedarse
 * sin tarjetas.
 */
export function evaluateCourseAchievements(
  courseSlug: string,
  ctx: AchievementContext,
): BadgeAchievement[] {
  const defs = COURSE_ACHIEVEMENTS[courseSlug];
  if (!defs) return evaluateAchievements(ctx);
  const signals = computeSignals(ctx);
  return defs.map((def) => ({
    slug: def.slug,
    title: def.title,
    description: def.description,
    icon: def.icon,
    unlocked: signals[def.rule],
  }));
}
